"use client";

import React from "react";

import {
  UserRound,
  Mail,
  ShieldCheck,
  CalendarDays,
  Clock3,
  LogIn,
} from "lucide-react";

import type {
  CandidateUser,
} from "../../types/candidateDashboard.types";

interface AccountInfoProps {
  candidate: CandidateUser;
}

function formatDate(date: string) {
  if (!date) return "-";

  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function AccountInfo({
  candidate,
}: AccountInfoProps) {
  return (
    <div
      className="
        rounded-2xl
        border
        border-slate-200
        bg-white
        p-4
        shadow-sm
        transition-all
        duration-200
        hover:border-blue-200
        hover:shadow-md
      "
    >
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <div
          className="
            flex
            h-10
            w-10
            shrink-0
            items-center
            justify-center
            rounded-xl
            bg-blue-100
            text-blue-600
          "
        >
          <UserRound
            size={20}
            strokeWidth={2}
          />
        </div>

        <div className="min-w-0">
          <h2
            className="
              text-base
              font-bold
              tracking-tight
              text-slate-900
            "
          >
            Account Information
          </h2>

          <p className="mt-0.5 text-xs text-slate-500">
            Candidate account details
          </p>
        </div>
      </div>

      {/* Information */}
      <div className="space-y-2">
        <InfoRow
          icon={<UserRound size={15} />}
          label="Name"
          value={candidate.name}
        />

        <InfoRow
          icon={<Mail size={15} />}
          label="Email"
          value={candidate.email}
        />

        <InfoRow
          icon={<ShieldCheck size={15} />}
          label="Role"
          value={candidate.role}
        />

        <InfoRow
          icon={<LogIn size={15} />}
          label="Login Method"
          value={candidate.authProvider || "-"}
        />

        <InfoRow
          icon={<CalendarDays size={15} />}
          label="Created"
          value={formatDate(candidate.createdAt)}
        />

        <InfoRow
          icon={<Clock3 size={15} />}
          label="Last Updated"
          value={formatDate(
            candidate.updatedAt ||
              candidate.createdAt
          )}
        />
      </div>
    </div>
  );
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoRow({
  icon,
  label,
  value,
}: InfoRowProps) {
  return (
    <div
      className="
        flex
        min-h-10
        items-center
        justify-between
        gap-3
        rounded-xl
        border
        border-slate-100
        bg-slate-50
        px-3
        py-2
        transition-colors
        duration-200
        hover:border-blue-100
        hover:bg-blue-50/40
      "
    >
      <div
        className="
          flex
          min-w-0
          items-center
          gap-2
          text-xs
          font-medium
          text-slate-500
        "
      >
        <span className="shrink-0 text-blue-600">
          {icon}
        </span>

        <span>{label}</span>
      </div>

      <strong
        className="
          max-w-[60%]
          truncate
          text-right
          text-xs
          font-semibold
          text-slate-900
        "
        title={value}
      >
        {value}
      </strong>
    </div>
  );
}